import React, {useEffect, useMemo, useState} from "react";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {IContrato} from "@models/contratos/contrato.ts";
import {useLocation, useNavigate, useSearchParams} from "react-router";
import {_ContratosEndpoints} from "@localization/siteMap/endpoints/_ContratosEndpoints.ts";
import {ContratoInformacao} from "@components/contratos/ContratoInformacao.tsx";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {Button, ButtonGroup, CircularProgress, Grid, Paper, Tab, Tabs} from "@mui/material";
import {SolicitacoesPage} from "@components/solicitacoes/pages/solicitacoes/SolicitacoesPage.tsx";
import {ContratoParcelasPage} from "@components/contratos/pages/ContratoParcelasPage.tsx";
import {SaveOutlined} from "@mui/icons-material";
import lodash, {pick} from "lodash";
import {RecursosMovimentacoesV2Page} from "@components/recurso/pages/movimentacoes/RecursosMovimentacoesV2Page.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useContrato} from "@components/contratos/hooks/useContrato.tsx";
import {ICreateContratoDto} from "@components/contratos/models/ICreateContratoDto.ts";

export interface ContratoPageProps extends DefaultPageProps {
}

type tabs = 'InformacoesBasicas' | 'Parcelas' | 'Solicitacoes' | 'Movimentacoes'

export const ContratoPage: React.FC<ContratoPageProps> = (props) => {
	const navigate = useNavigate()
	const location = useLocation()

	const {Get} = useApi()
	const {createAlertFromResponse} = useAlert()
	const {undraftContrato, updateContrato} = useContrato()
	const [tab, setTab] = useState<tabs>('InformacoesBasicas')
	const [refreshRequested, setRefreshRequested] = useState(false);

	const [contrato, setContrato] = useState<IContrato | undefined>();
	const [newContrato, setNewContrato] = useState<(Partial<ICreateContratoDto> & {
		idContrato: number,
		isUsaDiferimento?: boolean,
		isTerminoIndefinido?: boolean,
	}) | undefined>();
	const [loading, setLoading] = useState(false);
	const [searchParams] = useSearchParams()
	const [contratoId] = useState(searchParams.get('contratoId'))

	useEffect(() => {
		setTab(location.hash.slice(1) as tabs || 'InformacoesBasicas')
	}, [location.hash]);

	useEffect(() => {
		if (contratoId) {
			setLoading(true)

			Get(_ContratosEndpoints.GetContratos({ids: [Number(contratoId)], deveIncluir: ['Parcelas', 'Fornecedor']}, {
				page: 1,
				pageSize: 1
			}), refreshRequested).then(x => {
				if (x.status == 'Success') {
					setContrato(x.items[0])
				}
			}).finally(() => {
				setLoading(false)
				setRefreshRequested(false)
			})
		}
	}, [contratoId, refreshRequested]);

	const hasChanges = useMemo(() => {
		const c = (v: any) => {
			return pick<typeof newContrato>(v, ['valor', 'descricao', 'nome', 'dataFim', 'fornecedorId', 'isUsaDiferimento', 'isTerminoIndefinido']);
		}
		return !lodash.isEqual(c(newContrato), c(contrato))
	}, [contrato, newContrato]);


	return (<DefaultContainer titulo={props.hideHeader ? '' : 'Contrato'} component={Paper}>
		{contrato && <Grid>
			<ContratoInformacao removeOpenButton value={contrato} variant={'list'}/>
			{loading && <Grid container justifyContent={'center'}>
				<CircularProgress/>
			</Grid>}

			<Tabs value={tab} onChange={(_e, v) => {
				navigate({
					pathname: location.pathname,
					search: location.search,
					hash: v
				})
			}}>
				<Tab value={'InformacoesBasicas'} label={'Informacoes Basicas'}/>
				<Tab value={'Solicitacoes'} label={'Solicitações'}/>
				<Tab value={'Parcelas'} label={'Parcelas'}/>
				<Tab value={'Movimentacoes'} label={'Movimentações'}/>
			</Tabs>

			{tab === 'InformacoesBasicas' && <Grid container>
				<Grid size={12} pb={1}>
					<Grid container justifyContent={'space-between'}>
						<Grid/>
						<Grid>
							<ButtonGroup>
								{contrato &&
									<Button disabled={!contrato.isDraft}
									        color={'warning'}
									        onClick={() => undraftContrato({idContrato: contrato?.id})
										        .then(x => {
											        createAlertFromResponse(x)
										        })}>Ativar contrato</Button>}
								{contrato && <Button
									disabled={!hasChanges}
									variant={'outlined'} color={'success'} endIcon={<SaveOutlined/>}
									onClick={() => {
										if (newContrato) {
											updateContrato({
												...newContrato,
												idContrato: contrato?.id,
											}).then(x => {
												if (x.status === 'Success') {
													setRefreshRequested(true);
												}
												createAlertFromResponse(x)
											});
										}
									}}>Salvar</Button>}
							</ButtonGroup>
						</Grid>
					</Grid>
				</Grid>
				<Grid size={12}>
					<ContratoInformacao value={contrato}
					                    variant={'grid'}
					                    onChange={(v) => {
						                    setNewContrato({...v, idContrato: contrato.id});
					                    }}/>
				</Grid>
			</Grid>}

			{tab === 'Solicitacoes' && <Grid container>
				<Grid size={12}>
					<SolicitacoesPage filtros={{contratoId: [contrato.id]}}/>
				</Grid>
			</Grid>}

			{tab === 'Parcelas' && <Grid>
				<Grid size={12}>
					<ContratoParcelasPage
						preset={{contrato}}
						onSave={() => setRefreshRequested(true)}/>
				</Grid>
			</Grid>}
			{tab === 'Movimentacoes' && <Grid>
				<Grid size={12}>
					<RecursosMovimentacoesV2Page filters={{contratoId: [contrato.id]}}/>
				</Grid>
			</Grid>}
		</Grid>}
	</DefaultContainer>)
}